import {
  EdgeProps,
  BaseEdge,
  EdgeToolbar,
  EdgeLabelRenderer,
  Node,
} from '@xyflow/react';
import {
  ORTHO_EDGE_NODE_PADDING,
  ORTHO_EDGE_CORNER_RADIUS,
} from '@/constants/workflow-editor';
import { RotateCcw, Scale, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMemo } from 'react';
import { useEdgeInteraction } from './use-edge-interaction';
import {
  REPEAT_BACK_EDGE_ROLE,
  REPEAT_CONDITION_ROLE,
} from '@/pages/workflow/helper/repeat-control';
import './style.css';

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function getNodeRects(nodes: Node[], excludeIds: Set<string>): Rect[] {
  return nodes
    .filter((n) => !excludeIds.has(n.id))
    .map((n) => ({
      x: n.position.x - ORTHO_EDGE_NODE_PADDING,
      y: n.position.y - ORTHO_EDGE_NODE_PADDING,
      w: (n.measured?.width ?? 300) + ORTHO_EDGE_NODE_PADDING * 2,
      h: (n.measured?.height ?? 200) + ORTHO_EDGE_NODE_PADDING * 2,
    }));
}

function segmentIntersectsRect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  r: Rect,
): boolean {
  if (x1 === x2) {
    // Vertical segment
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    return x1 > r.x && x1 < r.x + r.w && maxY > r.y && minY < r.y + r.h;
  }
  // Horizontal segment
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  return y1 > r.y && y1 < r.y + r.h && maxX > r.x && minX < r.x + r.w;
}

function buildOrthogonalPath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  rects: Rect[],
): string {
  // Simple left-to-right routing: exit right, enter left.
  // Try midpoint-X first; if that vertical segment hits a node, route above or below.
  const midX = (sx + tx) / 2;

  const verticalSegmentHits = rects.some((r) =>
    segmentIntersectsRect(midX, Math.min(sy, ty), midX, Math.max(sy, ty), r),
  );

  if (!verticalSegmentHits || Math.abs(sy - ty) < 5) {
    // Simple 3-segment: right → down/up → right
    return roundedPath(
      [
        [sx, sy],
        [midX, sy],
        [midX, ty],
        [tx, ty],
      ],
      ORTHO_EDGE_CORNER_RADIUS,
    );
  }

  // Find clear vertical channel by scanning for a gap outside all node rects
  let bestX = midX;
  let found = false;
  const minX = Math.min(sx, tx);
  const maxX = Math.max(sx, tx);

  // Try shifting the vertical segment left or right in 30px increments
  for (let offset = 30; offset < 500; offset += 30) {
    const tryLeft = midX - offset;
    const tryRight = midX + offset;

    // Prefer the side closer to the midpoint
    for (const candidateX of [tryRight, tryLeft]) {
      if (candidateX <= minX + 10 || candidateX >= maxX - 10) continue;
      const hits = rects.some((r) =>
        segmentIntersectsRect(
          candidateX,
          Math.min(sy, ty) - 5,
          candidateX,
          Math.max(sy, ty) + 5,
          r,
        ),
      );
      if (!hits) {
        bestX = candidateX;
        found = true;
        break;
      }
    }
    if (found) break;
  }

  if (!found) {
    // Route above or below all obstacles
    const allTops = rects.map((r) => r.y);
    const allBottoms = rects.map((r) => r.y + r.h);
    const topClear = Math.min(...allTops) - 40;
    const bottomClear = Math.max(...allBottoms) + 40;

    // Pick whichever is closer to the average of sy/ty
    const avgY = (sy + ty) / 2;
    const routeY =
      Math.abs(topClear - avgY) < Math.abs(bottomClear - avgY)
        ? topClear
        : bottomClear;

    return roundedPath(
      [
        [sx, sy],
        [midX, sy],
        [midX, routeY],
        [tx, routeY],
        [tx, ty],
      ],
      ORTHO_EDGE_CORNER_RADIUS,
    );
  }

  return roundedPath(
    [
      [sx, sy],
      [bestX, sy],
      [bestX, ty],
      [tx, ty],
    ],
    ORTHO_EDGE_CORNER_RADIUS,
  );
}

function roundedPath(points: [number, number][], radius: number): string {
  const deduped = points.filter(
    (p, i) => i === 0 || p[0] !== points[i - 1][0] || p[1] !== points[i - 1][1],
  );
  if (deduped.length < 2) return '';
  const parts: string[] = [`M ${deduped[0][0]} ${deduped[0][1]}`];

  for (let i = 1; i < deduped.length - 1; i++) {
    const prev = deduped[i - 1];
    const curr = deduped[i];
    const next = deduped[i + 1];

    const dx1 = curr[0] - prev[0];
    const dy1 = curr[1] - prev[1];
    const dx2 = next[0] - curr[0];
    const dy2 = next[1] - curr[1];

    const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

    if (len1 === 0 || len2 === 0) {
      parts.push(`L ${curr[0]} ${curr[1]}`);
      continue;
    }

    const r = Math.min(radius, len1 / 2, len2 / 2);

    const enterX = curr[0] - (dx1 / len1) * r;
    const enterY = curr[1] - (dy1 / len1) * r;
    const exitX = curr[0] + (dx2 / len2) * r;
    const exitY = curr[1] + (dy2 / len2) * r;

    parts.push(`L ${enterX} ${enterY}`);
    parts.push(`Q ${curr[0]} ${curr[1]} ${exitX} ${exitY}`);
  }

  const last = deduped[deduped.length - 1];
  parts.push(`L ${last[0]} ${last[1]}`);
  return parts.join(' ');
}

export default function OrthogonalEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  selected,
}: EdgeProps) {
  const {
    currentEdge,
    nodes,
    strokeColor,
    showDeleteToolbar,
    handleEdgeClick,
    handleDeleteClick,
    isConditional,
    branchLabel,
    isSelectedBranch,
    hasSelectedBranch,
  } = useEdgeInteraction(id, selected);
  const repeatRole = (currentEdge?.data as { repeat_role?: string } | undefined)
    ?.repeat_role;
  const isRepeatBackEdge = repeatRole === REPEAT_BACK_EDGE_ROLE;
  const isRepeatConditionEdge = repeatRole === REPEAT_CONDITION_ROLE;

  const edgePath = useMemo(() => {
    const excludeIds = new Set<string>();
    if (currentEdge) {
      excludeIds.add(currentEdge.source);
      excludeIds.add(currentEdge.target);
    }
    const rects = getNodeRects(nodes, excludeIds);
    return buildOrthogonalPath(sourceX, sourceY, targetX, targetY, rects);
  }, [sourceX, sourceY, targetX, targetY, nodes, currentEdge]);

  const centerX = (sourceX + targetX) / 2;
  const centerY = (sourceY + targetY) / 2;

  const edgeStyle = useMemo(
    () => ({
      ...style,
      stroke: strokeColor,
      strokeWidth:
        isConditional || isRepeatBackEdge || isRepeatConditionEdge
          ? isConditional && isSelectedBranch
            ? 3
            : 2.5
          : 2.5,
      ...(isRepeatBackEdge ? { strokeDasharray: '7 5' } : {}),
      ...(isConditional
        ? {
            strokeDasharray: '7 5',
            opacity: hasSelectedBranch && !isSelectedBranch ? 0.38 : 1,
          }
        : {}),
    }),
    [
      style,
      strokeColor,
      isConditional,
      isRepeatBackEdge,
      isRepeatConditionEdge,
      isSelectedBranch,
      hasSelectedBranch,
    ],
  );

  return (
    <>
      <path
        d={edgePath}
        fill='none'
        stroke='transparent'
        strokeWidth={30}
        className='react-flow__edge-path edge-hitbox'
        onClick={handleEdgeClick}
        style={{ cursor: 'pointer', stroke: 'transparent' }}
      />
      <BaseEdge
        id={id}
        path={edgePath}
        style={edgeStyle}
        className={`react-flow__edge-path ${selected ? 'edge-path-selected' : ''}`}
        pointerEvents='none'
      />
      {isConditional && branchLabel && (
        <EdgeLabelRenderer>
          <div
            className={`conditional-edge-label ${
              isSelectedBranch ? 'conditional-edge-label-selected' : ''
            }`}
            style={{
              transform: `translate(-50%, -50%) translate(${centerX}px, ${centerY}px)`,
              opacity: hasSelectedBranch && !isSelectedBranch ? 0.5 : 1,
            }}
            data-testid={`conditional-edge-label-${branchLabel}`}
          >
            {branchLabel}
          </div>
        </EdgeLabelRenderer>
      )}
      {(isRepeatBackEdge || isRepeatConditionEdge) && (
        <EdgeLabelRenderer>
          <div
            className={`pointer-events-none absolute flex items-center gap-1 rounded-full border bg-background px-2 py-1 text-[10px] font-semibold shadow-sm ${
              isRepeatBackEdge
                ? 'border-amber-400 text-amber-800'
                : 'border-violet-300 text-violet-800'
            }`}
            style={{
              transform: `translate(-50%, -50%) translate(${centerX}px, ${centerY}px)`,
            }}
          >
            {isRepeatBackEdge ? (
              <RotateCcw className='size-3' />
            ) : (
              <Scale className='size-3' />
            )}
            {isRepeatBackEdge ? 'Repeat + feedback' : 'Evaluator / condition'}
          </div>
        </EdgeLabelRenderer>
      )}
      {showDeleteToolbar && (
        <EdgeToolbar edgeId={id} x={centerX} y={centerY}>
          <div className='edge-toolbar-wrapper'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleDeleteClick}
              aria-label='Delete edge'
              title='Delete edge'
            >
              <Trash2 size={16} />
              Delete
            </Button>
          </div>
        </EdgeToolbar>
      )}
    </>
  );
}
